import { Component, inject, OnInit, TemplateRef, viewChild } from '@angular/core';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { ActivatedRoute, RouterLink, RouterLinkActive } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ProcessesTableComponent } from '../processes-table/processes-table.component';
import { BrowserMessagesService, ExportService, LoaderComponent, ModalRef, ModalService } from '@rbb/ng-web-template-lib';
import { ProcessService } from '../../services/process.service';
import { FormService } from '../../services/form.service';
import { ProcessListItem } from '../../data/process-list-item';
import { FormListItem } from '../../data/form-list-item';
import { Observable } from 'rxjs';
import { ProcessesViewType } from '../../types/processes-view-type';

@Component({
  selector: 'app-process',
  templateUrl: './processes.component.html',
  styleUrls: ['./processes.component.css'],
  imports: [RouterLink, RouterLinkActive, FormsModule, ProcessesTableComponent, LoaderComponent]
})
export class ProcessesComponent implements OnInit {
  protected processService = inject(ProcessService);
  private browserMessagesService = inject(BrowserMessagesService);
  private route = inject(ActivatedRoute);
  private formService = inject(FormService);
  private modalService = inject(ModalService);
  private exportService = inject(ExportService);

  type: ProcessesViewType;
  tab: string | null;
  tabs = new Map<string, string>();
  gets = new Map<string, (from: string | null, to: string | null) => Observable<ProcessListItem[]>>();
  getFormsFunc: () => Observable<FormListItem[]>;
  exportFunc: (formId: string, from: string, to: string) => Observable<HttpResponse<Blob>>;

  processes: ProcessListItem[] | undefined;

  from: string | null;
  to: string | null = null;

  forms = [] as FormListItem[];
  selectedForm: FormListItem | undefined;
  exportFrom: string | undefined;
  exportTo: string | undefined;

  readonly ngModal = viewChild.required<TemplateRef<unknown>>('ngModal');
  ngModalService: ModalRef<unknown> | undefined;

  constructor() {
    this.type = this.route.snapshot.data['type'] as ProcessesViewType;
    const d = new Date();
    switch (this.type) {
      case ProcessesViewType.MY:
        d.setDate(new Date().getDate() - 90);
        this.tabs.set('my', 'Meine Anträge');
        this.gets.set('my', (from: string | null, to: string | null) => this.processService.getMyProcesses(from, to));
        this.getFormsFunc = (): Observable<FormListItem[]> => this.formService.getFormsForMe();
        this.exportFunc = (formId: string, from: string, to: string): Observable<HttpResponse<Blob>> => this.processService.exportMyProcesses(formId, from, to);
        break;
      case ProcessesViewType.APPROVER:
        d.setDate(new Date().getDate() - 90);
        this.tabs.set('new', 'Neue Anträge zum Prüfen');
        this.gets.set('new', () => this.processService.getProcessesToApprove());
        this.tabs.set('old', 'Bereits geprüfte Anträge');
        this.gets.set('old', (from: string | null, to: string | null) => this.processService.getOldProcessesAsApprover(from, to));
        this.getFormsFunc = (): Observable<FormListItem[]> => this.formService.getFormsForApprover();
        this.exportFunc = (formId: string, from: string, to: string): Observable<HttpResponse<Blob>> => this.processService.exportProcessesForApprovers(formId, from, to);
        break;
      case ProcessesViewType.OPERATOR:
        d.setDate(new Date().getDate() - 30);
        this.tabs.set('open', 'Ausstehende zu bearbeitende Anträge');
        this.gets.set('open', () => this.processService.getProcessesOpenForProcessing());
        this.tabs.set('all', 'Alle zu bearbeitende Anträge');
        this.gets.set('all', (from: string | null, to: string | null) => this.processService.getProcessesToProcessing(from, to));
        this.getFormsFunc = (): Observable<FormListItem[]> => this.formService.getFormsForOperator();
        this.exportFunc = (formId: string, from: string, to: string): Observable<HttpResponse<Blob>> => this.processService.exportProcessesForOperators(formId, from, to);
        break;
    }
    this.from = d.toISOString().slice(0, 10);
    this.tab = '';
    this.route.paramMap.subscribe((paramMap) => {
      if (this.tab !== paramMap.get('tab')) {
        this.tab = paramMap.get('tab');
        this.load();
      }
    });
    this.tab = this.route.snapshot.paramMap.get('tab');
  }

  ngOnInit(): void {
    this.load();
    this.getForms();
  }

  load(): void {
    const tab = this.tab;
    if (tab) {
      this.gets.get(tab)?.(this.from, this.to)?.subscribe({
        next: (next) => {
          if (this.tab === tab) {
            this.processes = next;
          }
        },
        error: (error) => {
          this.browserMessagesService.showHttpError(error, 'Anträge konnten nicht geladen werden');
        }
      });
    }
  }

  getForms(): void {
    this.getFormsFunc().subscribe({
      next: (next) => {
        this.forms = next;
        if (this.forms?.length) {
          this.selectedForm = this.forms[0];
        }
      },
      error: (error) => {
        this.browserMessagesService.showHttpError(error, 'Formulare konnten nicht geladen werden');
      }
    });
  }

  export(): void {
    if (this.selectedForm?.id) {
      this.openModal();
      this.exportFunc(this.selectedForm.id, this.exportFrom ?? '', this.exportTo ?? '').subscribe({
        next: (data) => {
          this.exportService.createDownloadableFileFromHttpResponse(data, 'antrag.pdf');
          this.closeModal();
        },
        error: (error: HttpErrorResponse) => {
          this.closeModal();
          this.browserMessagesService.showHttpError(error, 'Fehler');
        }
      });
    }
  }

  openModal(): void {
    this.ngModalService = this.modalService.open(this.ngModal(), { centered: true, backdrop: 'static' });
  }

  closeModal(): void {
    this.ngModalService?.close();
  }

  protected readonly ProcessesViewType = ProcessesViewType;
}
